import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Loader2, Send, Sparkles, PhoneCall, AlertTriangle, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "Hi coach. I'm Sideline — a private space to talk through the stuff that's on your plate. Training, parents, players, balance, doubt — whatever it is. Where do you want to start?",
};

const HELPLINE_FOOTER = (
  <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm" data-testid="welfare-helpline-footer">
    <p className="font-medium text-rose-900 flex items-center gap-1.5"><PhoneCall className="w-4 h-4" />If you're struggling right now</p>
    <p className="text-rose-800 mt-1">
      Please contact <strong>Samaritans</strong> on <a className="underline" href="tel:116123">116 123</a> (free, 24/7, UK) or your local emergency services.
      This chat is supportive but is not a substitute for professional help.
    </p>
  </div>
);

export default function WelfareChat({ optInEnabled, onRequestEnable }) {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [crisisShown, setCrisisShown] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!optInEnabled) {
    return (
      <Card data-testid="welfare-chat-optin-required">
        <CardContent className="py-12 text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-3 text-rose-300" strokeWidth={1.5} />
          <h3 className="text-lg font-semibold text-gray-900">AI support chat is off</h3>
          <p className="text-gray-500 mt-1 max-w-md mx-auto">
            Sideline is a private AI companion for grassroots coaches. Turn it on in Settings whenever you're ready.
            It's supportive — not a substitute for professional help.
          </p>
          <Button
            onClick={onRequestEnable}
            className="mt-4 bg-rose-500 hover:bg-rose-600 text-white"
            data-testid="welfare-chat-enable-btn"
          >
            Open Settings
          </Button>
        </CardContent>
      </Card>
    );
  }

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(`${API}/welfare/chat`, {
        message: text,
        session_id: sessionId,
      });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
      if (res.data.crisis_detected) setCrisisShown(true);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to send message';
      toast.error(detail);
      setMessages(prev => [...prev, { role: 'assistant', content: `_(${detail})_`, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const handleNewSession = () => {
    setMessages([WELCOME_MESSAGE]);
    setSessionId(null);
    setCrisisShown(false);
  };

  return (
    <Card data-testid="welfare-chat-container">
      <CardContent className="p-0 flex flex-col h-[600px]">
        {/* Header */}
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-rose-500" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Sideline</p>
              <p className="text-xs text-gray-500">Private &amp; supportive — not professional advice</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleNewSession} data-testid="welfare-chat-new-session">
            <Plus className="w-3.5 h-3.5 mr-1" />New
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`welfare-chat-message-${i}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 whitespace-pre-wrap text-sm ${
                  m.role === 'user'
                    ? 'bg-rose-500 text-white rounded-br-sm'
                    : m.error
                      ? 'bg-red-50 border border-red-200 text-red-700 rounded-bl-sm'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="welfare-chat-typing">
              <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm">
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              </div>
            </div>
          )}
          <div ref={scrollRef} />
        </div>

        {/* Crisis banner */}
        {crisisShown && (
          <div className="px-4 pt-3 border-t border-rose-200 bg-rose-50">
            <div className="flex items-start gap-2 text-sm text-rose-900">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                <strong>You don't have to do this alone.</strong> Samaritans (UK): <a href="tel:116123" className="underline">116 123</a> — free, 24/7.
                In immediate danger, call 999.
              </span>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="border-t border-gray-200 p-3 bg-white">
          <div className="flex gap-2 items-end">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="What's on your mind, coach?"
              rows={2}
              className="resize-none flex-1"
              disabled={sending}
              data-testid="welfare-chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className="bg-rose-500 hover:bg-rose-600 text-white h-auto"
              data-testid="welfare-chat-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          {HELPLINE_FOOTER}
        </div>
      </CardContent>
    </Card>
  );
}
